/**
 * i18n Configuration
 *
 * English is the primary language and ships in the initial bundle as
 * the fallback. Spanish is dynamic-imported only when active to keep
 * cold-start parsing cheap for English users (es.json ≈ 329 KB raw).
 */

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import en from './locales/en.json';

export const supportedLanguages = ['en', 'es'] as const;
export type SupportedLanguage = typeof supportedLanguages[number];

// Synchronous detection so we can decide what to bundle before init.
// Mirrors `LanguageDetector`'s `['localStorage', 'navigator']` order.
function detectInitialLanguage(): SupportedLanguage {
  try {
    const stored = typeof localStorage !== 'undefined' ? localStorage.getItem('i18nextLng') : null;
    if (stored?.startsWith('es')) return 'es';
    if (stored?.startsWith('en')) return 'en';
  } catch {
    /* localStorage may be unavailable (SSR, privacy mode) */
  }
  if (typeof navigator !== 'undefined' && navigator.language?.startsWith('es')) return 'es';
  return 'en';
}

const initialLang = detectInitialLanguage();

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: { en: { translation: en } },
    fallbackLng: 'en',
    lng: initialLang,
    supportedLngs: supportedLanguages,
    partialBundledLanguages: true,

    detection: {
      order: ['localStorage', 'navigator', 'htmlTag'],
      caches: ['localStorage'],
      lookupLocalStorage: 'i18nextLng',
    },

    interpolation: {
      escapeValue: false, // React already escapes values
    },

    react: {
      useSuspense: false,
    },
  });

// Load Spanish in the background if it's the active language. Strings
// briefly fall back to English on first paint until the chunk arrives.
async function loadLanguageBundle(lng: SupportedLanguage): Promise<void> {
  if (lng === 'en') return; // bundled eagerly
  if (i18n.hasResourceBundle(lng, 'translation')) return;
  const mod = await import('./locales/es.json');
  i18n.addResourceBundle(lng, 'translation', mod.default, true, true);
}

if (initialLang !== 'en') {
  void loadLanguageBundle(initialLang);
}

// Public helper for runtime language switches — ensures the bundle is
// loaded before flipping i18n.language.
export async function changeLanguage(lng: SupportedLanguage): Promise<void> {
  await loadLanguageBundle(lng);
  await i18n.changeLanguage(lng);
}

export default i18n;
